import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { sseChannelUrl } from "../components/Core/SSE/streamApiRoot";
import {
	AiApprovalDecision,
	AiConversation,
	AiToolDescriptor,
	AiTurnDataset,
	AiTurnEvent,
} from "../models/AiAssistant";
import { ApiErrorResponse } from "../models/ApiResponse";
import { apiStore } from "../stores/api.store";
import { useAuthStore } from "../stores/auth.store";

const CONVERSATIONS_KEY = ["ai-conversations"];
const conversationKey = (conversationId?: string) => ["ai-conversation", conversationId];
const datasetKey = (conversationId?: string, datasetId?: string) => ["ai-dataset", conversationId, datasetId];

/** api-gateway SSE channel the service publishes turn events on (AI_SSE_CHANNEL). */
const AI_SSE_CHANNEL = "ai-assistant";

/**
 * How often a running conversation is refetched. The SSE event normally gets
 * there first; this is the fallback when the stream is blocked or dropped.
 */
const RUNNING_POLL_INTERVAL_MS = 3000;

/**
 * The user's conversations, newest first. Only fetched while the assistant
 * sidebar is open (`enabled`).
 */
export function useAiConversations(enabled: boolean) {
	const { data, ...rest } = useQuery(
		CONVERSATIONS_KEY,
		() =>
			apiStore
				.get<{ conversations: AiConversation[] }>({
					path: "/ai/conversation",
					isAuthRequired: true,
				})
				.then((d) => d.data.conversations),
		{ enabled, staleTime: 30 * 1000 }
	);

	return { conversations: data || [], ...rest };
}

export function useAiConversation(conversationId?: string) {
	const { data, ...rest } = useQuery(
		conversationKey(conversationId),
		() =>
			apiStore
				.get<AiConversation>({
					path: `/ai/conversation/${conversationId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			enabled: !!conversationId,
			// The accepted conversation is written straight into this cache
			// entry by the mutations below; never refetch on focus in the
			// middle of a chat.
			refetchOnWindowFocus: false,
			staleTime: 60 * 1000,
			// A turn runs on the server after the request returned; keep
			// looking until it has landed.
			refetchInterval: (current) => (current?.status === "running" ? RUNNING_POLL_INTERVAL_MS : false),
		}
	);

	return { conversation: data, ...rest };
}

const fetchDataset = (conversationId: string, datasetId: string) =>
	apiStore
		.get<AiTurnDataset>({
			path: `/ai/conversation/${conversationId}/dataset/${datasetId}`,
			isAuthRequired: true,
		})
		.then((d) => d.data);

/**
 * Every row of one table under an answer. The conversation carries a preview
 * per table; this is fetched when the table is opened or exported. A table
 * never changes once its turn has landed, so it is cached for the session.
 */
export function useAiDataset(conversationId?: string, datasetId?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		datasetKey(conversationId, datasetId),
		() => fetchDataset(conversationId as string, datasetId as string),
		{ enabled: enabled && !!conversationId && !!datasetId, staleTime: Infinity, cacheTime: 30 * 60 * 1000 }
	);

	return { dataset: data, ...rest };
}

/** Imperative counterpart of `useAiDataset`, for the CSV export; shares its cache. */
export function useFetchAiDataset() {
	const queryClient = useQueryClient();
	return (conversationId: string, datasetId: string) =>
		queryClient.fetchQuery(datasetKey(conversationId, datasetId), () => fetchDataset(conversationId, datasetId), {
			staleTime: Infinity,
		});
}

/**
 * Listens for "turn landed" events for the logged-in user while `enabled`
 * and refetches the conversation they belong to. One EventSource for the
 * whole sidebar, opened on demand so idle tabs hold no stream.
 */
export function useAiTurnEvents(enabled: boolean) {
	const queryClient = useQueryClient();
	const accessToken = useAuthStore((state) => state.accessToken);

	useEffect(() => {
		if (!enabled || !accessToken || typeof EventSource === "undefined") return;

		let source: EventSource | null = null;
		let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
		let attempts = 0;
		let closed = false;

		const connect = () => {
			if (closed) return;
			source = new EventSource(sseChannelUrl(AI_SSE_CHANNEL, accessToken));
			let openedAt = 0;

			source.onopen = () => {
				openedAt = Date.now();
			};

			source.onmessage = (message) => {
				let event: Partial<AiTurnEvent> & { message?: string } = {};
				try {
					event = JSON.parse(message.data);
				} catch {
					return;
				}
				if (event.type === "error") {
					// api-gateway refused the stream (e.g. "Maximum connections
					// exceeded"); retrying only adds to the count. Polling covers it.
					closed = true;
					source?.close();
					return;
				}
				if (event.type !== "turn" || !event.conversationId) return;
				queryClient.invalidateQueries(conversationKey(event.conversationId));
				queryClient.invalidateQueries(CONVERSATIONS_KEY);
			};

			source.onerror = () => {
				source?.close();
				source = null;
				if (closed) return;
				// A stream that lived a while was healthy: start the backoff over.
				// One that died at once (refused, gateway timeout) keeps growing it, up to a minute.
				attempts = openedAt && Date.now() - openedAt > 30 * 1000 ? 1 : attempts + 1;
				reconnectTimer = setTimeout(connect, Math.min(60 * 1000, 2000 * 2 ** Math.min(attempts, 5)));
			};
		};

		connect();

		return () => {
			closed = true;
			if (reconnectTimer) clearTimeout(reconnectTimer);
			source?.close();
		};
	}, [enabled, accessToken, queryClient]);
}

export function useCreateAiConversation() {
	const queryClient = useQueryClient();

	return useMutation<AiConversation, ApiErrorResponse, { title?: string } | void>(
		(request) =>
			apiStore
				.post<AiConversation>({
					path: "/ai/conversation",
					body: request || {},
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (conversation) => {
				queryClient.setQueryData(conversationKey(conversation.id), conversation);
				queryClient.invalidateQueries(CONVERSATIONS_KEY);
			},
		}
	);
}

/**
 * The message and approval endpoints answer 202 with the conversation as it
 * looks once the turn is accepted (`status: "running"`); that replaces the
 * optimistic copy, and the polling/SSE in `useAiConversation` takes it from
 * there until the assistant turn has landed.
 */
function applyAcceptedConversation(
	queryClient: ReturnType<typeof useQueryClient>,
	conversation: AiConversation
) {
	queryClient.setQueryData(conversationKey(conversation.id), conversation);
	queryClient.invalidateQueries(CONVERSATIONS_KEY);
}

export function useSendAiMessage() {
	const queryClient = useQueryClient();

	return useMutation<AiConversation, ApiErrorResponse, { conversationId: string; text: string }>(
		({ conversationId, text }) =>
			apiStore
				.post<AiConversation>({
					path: `/ai/conversation/${conversationId}/message`,
					body: { text },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onMutate: ({ conversationId, text }) => {
				queryClient.setQueryData<AiConversation | undefined>(
					conversationKey(conversationId),
					(current) =>
						current && {
							...current,
							title: current.title || text.slice(0, 60),
							turns: [
								...current.turns,
								{
									id: `local-${Date.now()}`,
									role: "user",
									text,
									created: new Date().toISOString(),
								},
							],
							// Sending a new message implicitly denies whatever was pending.
							pendingApprovals: [],
						}
				);
			},
			onSuccess: (conversation) => applyAcceptedConversation(queryClient, conversation),
			onError: (_error, { conversationId }) => {
				// Drop the optimistic user turn so the text can be re-sent.
				queryClient.setQueryData<AiConversation | undefined>(
					conversationKey(conversationId),
					(current) =>
						current && {
							...current,
							turns: current.turns.filter((turn) => !turn.id.startsWith("local-")),
						}
				);
			},
		}
	);
}

export function useRespondToAiApprovals() {
	const queryClient = useQueryClient();

	return useMutation<
		AiConversation,
		ApiErrorResponse,
		{ conversationId: string; decisions: AiApprovalDecision[] }
	>(
		({ conversationId, decisions }) =>
			apiStore
				.post<AiConversation>({
					path: `/ai/conversation/${conversationId}/approval`,
					body: { decisions },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (conversation) => applyAcceptedConversation(queryClient, conversation),
		}
	);
}

export function useAiTools(enabled: boolean) {
	const { data, ...rest } = useQuery(
		["ai-tools"],
		() =>
			apiStore
				.get<{ tools: AiToolDescriptor[] }>({ path: "/ai/tool", isAuthRequired: true })
				.then((d) => d.data.tools),
		{ enabled, staleTime: 10 * 60 * 1000 }
	);

	return { tools: data || [], ...rest };
}
